import {
  IconCalendar,
  IconCalendarEvent,
  IconCash,
  IconFileText,
  IconSettings,
  IconTag,
  IconUsers,
  TablerIconsProps,
} from '@tabler/icons-react';

/**
 * The thirteen simple onboarding steps, and everything that renders them.
 *
 * Copy, destinations, help articles and time estimates live here and only here, so a
 * wording change is a one-file edit. The `key` of each step is the same string the API
 * stores and the toggle action accepts.
 *
 * Completion is self-reported throughout. Nothing here is checked against the gym's real
 * configuration, in either direction.
 */

export const ONBOARDING_CATEGORIES = [
  'General Setup',
  'Calendar & Availability',
  'Products & Pricing',
  'Documents',
  'Scheduling',
  'Payroll Setup',
  'Staff Accounts',
] as const;

export type OnboardingCategory = (typeof ONBOARDING_CATEGORIES)[number];

export const ONBOARDING_CATEGORY_ICONS: Record<
  OnboardingCategory,
  (props: TablerIconsProps) => JSX.Element
> = {
  'General Setup': IconSettings,
  'Calendar & Availability': IconCalendar,
  'Products & Pricing': IconTag,
  Documents: IconFileText,
  Scheduling: IconCalendarEvent,
  'Payroll Setup': IconCash,
  'Staff Accounts': IconUsers,
};

const HELP_ARTICLE_BASE_URL = 'https://help.usekilo.com/kb/articles';

export interface OnboardingDestination {
  label: string;
  path: string;
}

export interface OnboardingHelpArticle {
  label: string;
  url: string;
}

export interface OnboardingStep {
  key: string;
  category: OnboardingCategory;
  title: string;
  /** Only Account Settings carries the badge: its defaults feed everything created later. */
  startHere?: boolean;
  /** Estimate for this step, and the unit the whole time-remaining figure is built from. */
  minutes: number;
  /** Repeated-action steps are estimated per item rather than as a flat total. */
  minutesPerUnit?: boolean;
  /** Qualifier appended to the estimate where the time sink is deciding, not entering. */
  minutesNote?: string;
  /** What the step buys the gym, shown above the instructions. */
  why: string;
  /** The numbered instructions. The first is linked to the primary destination. */
  instructions: string[];
  /** Rendered as a callout under the instructions, where a card needs a caveat. */
  note?: string;
  destinations: OnboardingDestination[];
  helpArticles: OnboardingHelpArticle[];
}

export const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    key: 'account_settings',
    category: 'General Setup',
    title: 'Update Your Account Settings',
    startHere: true,
    minutes: 10,
    minutesNote: 'once you know your policies',
    why: 'These settings feed everything else you build. Your business name and contact details appear on the invoices and emails your athletes receive, and the rules you set here become the defaults for every class and appointment you create later.',
    instructions: [
      'Click your name in the bottom left corner, then select Settings.',
      'Under General Settings, update your logo, business name, location, contact information, and Notify Email.',
      'Set your reservation, waitlist, and cancellation rules for Classes, Appointments, and the Athlete App.',
    ],
    destinations: [
      {
        label: 'Open Settings',
        path: '/gym-settings',
      },
    ],
    helpArticles: [
      {
        label: 'Update Your Account Settings in Kilo GMS',
        url: `${HELP_ARTICLE_BASE_URL}/update-account-settings`,
      },
    ],
  },
  {
    key: 'google_calendar',
    category: 'Calendar & Availability',
    title: 'Connect Google Calendar',
    minutes: 3,
    why: 'Connecting your Google Calendar lets Kilo account for commitments that live outside Kilo, so nobody schedules you into a conflict. It also puts your Kilo sessions on the calendar you already check.',
    instructions: [
      'Go to Calendar → Integration.',
      'Click Connect Google Calendar and sign in to your Google account.',
      'Check Select All on the Google permissions pop-up.',
      'Choose which event types sync, then pick the calendar they sync to.',
      'Click Save.',
    ],
    destinations: [
      {
        label: 'Open Integration',
        path: '/integrations/google-calendar',
      },
    ],
    helpArticles: [
      {
        label: 'Connect Google Calendar to GMS',
        url: `${HELP_ARTICLE_BASE_URL}/connect-google-calendar-to-gms`,
      },
    ],
  },
  {
    key: 'availability',
    category: 'Calendar & Availability',
    title: 'Set Your Availability',
    minutes: 5,
    why: 'Your availability tells Kilo when you can be assigned to coach. Every day starts marked as available around the clock, so until you replace that with your real hours, the schedule treats you as free at any time of day.',
    instructions: [
      'Go to Calendar → Availability.',
      'Edit your weekly schedule. Remove the default hours and add your real time windows for each day.',
      'Use Add Date and Time to override your availability on specific dates.',
    ],
    destinations: [
      {
        label: 'Open Availability',
        path: '/calendar/availability',
      },
    ],
    helpArticles: [
      {
        label: 'Set Your Availability in Kilo GMS',
        url: `${HELP_ARTICLE_BASE_URL}/coach-availability`,
      },
    ],
  },
  {
    key: 'package_categories',
    category: 'Products & Pricing',
    title: 'Create Package Categories',
    minutes: 3,
    why: 'Categories group your packages so athletes can find what they are looking for and your reporting stays organized. Creating them now means the category is ready to select when you build your packages.',
    instructions: [
      'Go to Products → Package Categories.',
      'Click + Category and give the category a name.',
      'Choose any existing packages that belong in this category, then click Save.',
    ],
    note: "If you haven't built your packages yet, that's expected. Create your categories now, and you'll select one as you build each package on and after your working session.",
    destinations: [
      {
        label: 'Open Package Categories',
        path: '/products/package-categories',
      },
    ],
    helpArticles: [
      {
        label: 'Create and Apply Package Categories',
        url: `${HELP_ARTICLE_BASE_URL}/categorize-packages`,
      },
    ],
  },
  {
    key: 'tax_rates',
    category: 'Products & Pricing',
    title: 'Set Your Tax Rates',
    minutes: 2,
    minutesPerUnit: true,
    why: 'Tax is applied per product, so your rates need to exist before you build packages or add retail items. Create one rate for each percentage your location requires.',
    instructions: [
      'Go to Products → Tax Rates.',
      'Select an existing rate, for example, Cancellation and No Show Tax Rate, or add a new one.',
      'Click Edit, set the percentage, then click Save.',
    ],
    destinations: [
      {
        label: 'Open Tax Rates',
        path: '/products/tax-rates',
      },
    ],
    helpArticles: [
      {
        label: 'Set Up Tax Rates in Kilo GMS',
        url: `${HELP_ARTICLE_BASE_URL}/set-up-tax-rates-in-kilo-gms`,
      },
    ],
  },
  {
    key: 'retail',
    category: 'Products & Pricing',
    title: 'Add Retail Items',
    minutes: 3,
    minutesPerUnit: true,
    why: 'Adding your retail items now means your team can charge an athlete for a drink or a shirt from day one, instead of stopping to build the product in the middle of a sale.',
    instructions: [
      'Go to Products → Retail Items.',
      'Click + Item and add a name, price, and tax rate.',
      "Click Save. The item is now available to charge from an athlete's cart.",
    ],
    destinations: [
      {
        label: 'Open Retail Items',
        path: '/products/retail',
      },
    ],
    helpArticles: [
      {
        label: 'Create and Charge for Retail Items',
        url: `${HELP_ARTICLE_BASE_URL}/create-and-charge-for-retail-items`,
      },
    ],
  },
  {
    key: 'fees_and_misc',
    category: 'Products & Pricing',
    title: 'Add Fees and Misc. Products',
    minutes: 3,
    minutesPerUnit: true,
    why: 'Fees and miscellaneous products cover one-time charges that are neither retail nor memberships, such as a late fee or a competition entry.',
    instructions: [
      'Go to Products → Fees & Misc. Products.',
      'Click + Product and enter a name, price, and tax rate.',
      "Click Save. You can then charge the product immediately, charge it on a schedule, or add it to the athlete's next invoice.",
    ],
    note: "Not every gym uses this. If you don't need it right now, check this step off and move on. You can add products here any time.",
    destinations: [
      {
        label: 'Open Fees & Misc.',
        path: '/products/fees-and-misc-products',
      },
    ],
    helpArticles: [
      {
        label: 'Create and Charge for Fees & Misc. Products',
        url: `${HELP_ARTICLE_BASE_URL}/charge-for-fees-and-miscellaneous-products`,
      },
    ],
  },
  {
    key: 'documents',
    category: 'Documents',
    title: 'Add Waiver and Contract Templates',
    minutes: 10,
    minutesNote: 'once your waiver wording is ready',
    why: 'Your waiver protects your gym and sets expectations with every athlete who walks in. Set it to generate automatically, and it will be waiting for each new athlete to sign before their first class.',
    instructions: [
      'Go to Documents → Templates.',
      'Click + Template and choose Waiver or Contract.',
      'Add a title and set your auto-generation settings.',
      'Insert personalization variables such as name, address, and package details.',
      'For a contract, assign the packages it applies to and add any sections that require initials.',
      'Click Save.',
    ],
    note: "If you sent us your waiver on your questionnaire, it's already here, and you can update it. If you haven't sent one yet, now is the time to add it, along with any contract templates you need.",
    destinations: [
      {
        label: 'Open Templates',
        path: '/documents/templates',
      },
    ],
    helpArticles: [
      {
        label: 'Create Waiver & Contract Templates',
        url: `${HELP_ARTICLE_BASE_URL}/create-waiver-contract-templates`,
      },
    ],
  },
  {
    key: 'locations',
    category: 'Scheduling',
    title: 'Add Your Locations',
    minutes: 2,
    minutesPerUnit: true,
    why: 'Locations are the spaces you run sessions in. Some gyms will only have one primary training space; others have several. Add any locations to which sessions will need to be assigned, for example Main Gym, PT Studio, or Consult Room.',
    instructions: [
      'Go to Events → Locations.',
      'Click + Location and give the location a name.',
      'Add a Zoom or Google Meet URL if you need one.',
      'Click Save, then repeat for each space you schedule into.',
    ],
    destinations: [
      {
        label: 'Open Locations',
        path: '/events/location',
      },
    ],
    helpArticles: [
      {
        label: 'Add Locations in Kilo GMS',
        url: `${HELP_ARTICLE_BASE_URL}/add-locations`,
      },
    ],
  },
  {
    key: 'events',
    category: 'Scheduling',
    title: 'Add Classes and Appointments',
    minutes: 10,
    minutesNote: 'for your first few types',
    why: 'These are the templates your schedule is built from, and your packages are built to include them. Creating all of them now means your packages can be set up properly in your working session.',
    instructions: [
      'Go to Events → Classes or Events → Appointments.',
      'Click + Class Type or + Appointment Type.',
      'Enter a name, then set the calendar color, location, capacity, duration, and reservation or cancellation rules.',
      'Click Save.',
    ],
    note: 'Complete Account Settings before this step. The reservation, waitlist, and cancellation rules you set there carry into every class and appointment type you create here, so setting them first saves you from entering the same rules over and over.',
    destinations: [
      {
        label: 'Open Classes',
        path: '/events/classes',
      },
      {
        label: 'Open Appointments',
        path: '/events/appointments',
      },
    ],
    helpArticles: [
      {
        label: 'Add Class and Appointment Types',
        url: `${HELP_ARTICLE_BASE_URL}/add-appointment-and-class-events`,
      },
    ],
  },
  {
    key: 'pay_rates',
    category: 'Payroll Setup',
    title: 'Create Pay Rates',
    minutes: 5,
    why: 'Pay rates need to exist before sessions go on your schedule, because payroll calculates from the rate attached to each coach as the session runs.',
    instructions: [
      'Go to Payroll → Pay Rates.',
      'Click the blue action button, then Add Pay Rate.',
      'Enter a Name for the rate.',
      'Select Hourly Pay Rate, Base Pay Rate, or Percentage-Based Pay Rate.',
      'Enter the Pay Rate, then click Save.',
    ],
    destinations: [
      {
        label: 'Open Pay Rates',
        path: '/payroll/pay-rates',
      },
    ],
    helpArticles: [
      {
        label: 'Payroll Overview',
        url: `${HELP_ARTICLE_BASE_URL}/payroll-features-overview`,
      },
      {
        label: 'Create Pay Rates',
        url: `${HELP_ARTICLE_BASE_URL}/create-pay-rates`,
      },
    ],
  },
  {
    key: 'staff_accounts',
    category: 'Staff Accounts',
    title: 'Add Staff and Set Permissions',
    minutes: 5,
    minutesPerUnit: true,
    minutesNote: 'for your first staff member',
    why: 'Adding your team now gives them time to set up their own calendars and availability before your first working session. Permissions control what each person sees, so a coach and a manager can work from the same system very differently.',
    instructions: [
      'Go to Users → Staff.',
      "Click + Staff and enter the staff member's contact information.",
      'Check Coach if this person will lead classes or appointments.',
      'Set their permissions from the dropdown: Calendar, Products, Users, Documents, Invoices, Payroll, and Gym Settings.',
      'Click Save.',
    ],
    destinations: [
      {
        label: 'Open Staff',
        path: '/users/staff',
      },
    ],
    helpArticles: [
      {
        label: 'Manage Staff Profiles and Permissions',
        url: `${HELP_ARTICLE_BASE_URL}/understand-staff-profiles-and-permissions`,
      },
    ],
  },
  {
    key: 'staff_availability',
    category: 'Staff Accounts',
    title: 'Staff Set Up Calendars and Availability',
    minutes: 2,
    minutesNote: 'to send the instructions. Your team does the rest',
    why: "Kilo builds your schedule around who is actually available, so every coach needs their own hours on file. This is one step you can't do for your team, so they'll need to log in to Kilo to set it up.",
    instructions: [
      'Ask each staff member to connect their own Google Calendar in Calendar → Integration.',
      'Ask them to set their weekly availability and any date-specific overrides in Calendar → Availability.',
    ],
    note: "Each staff member has to do this from their own account. There is no way to connect a calendar or set availability on someone else's behalf, so this step is done once each person has logged in. Feel free to share the Knowledge Base articles below for them to follow once they're logged in.",
    destinations: [],
    helpArticles: [
      {
        label: 'Connect Google Calendar to GMS',
        url: `${HELP_ARTICLE_BASE_URL}/connect-google-calendar-to-gms`,
      },
      {
        label: 'Set Your Availability in Kilo GMS',
        url: `${HELP_ARTICLE_BASE_URL}/coach-availability`,
      },
    ],
  },
];

/** The per-card estimate, e.g. "About 3 mins per item". */
export const formatStepEstimate = (step: OnboardingStep): string => {
  const unit = step.minutesPerUnit ? ' per item' : '';
  const note = step.minutesNote ? `, ${step.minutesNote}` : '';
  return `About ${step.minutes} min${step.minutes === 1 ? '' : 's'}${unit}${note}`;
};

/** The time-remaining figure beside the count, e.g. "53 min" or "1.1 hours". */
export const formatMinutesLeft = (minutes: number): string => {
  if (minutes <= 0) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.round((minutes / 60) * 10) / 10;
  return hours === 1 ? '1 hour' : `${hours} hours`;
};
